// Directe PV→verbruik vandaag, geïntegreerd per 5-min historie-bucket.
//
// Wachtrij #492: de EigenverbruikDuoTile rekende met min(PV_dag, Load_dag).
// Die ene teller voedde zowel PV-dekking (÷ load) als PV-zelfverbruik (÷ PV),
// zodat er altijd één van de twee op 100% stond: PV < load → zelfverbruik 100,
// PV ≥ load → dekking 100. Gemeten 07-13 oktober 2026 (savings_buckets_5min):
// op VJ stond zelfverbruik alle zeven dagen op 100% waar per bucket 26-91%
// klopte, en de dekking stond 2-4× te hoog (57-95% tegen 12-50%).
//
// Per bucket is het directe deel min(pv, last): PV die op dat moment
// verbruikt werd. PV die de accu in ging telt niet mee — dat is de definitie
// die de tegel belooft ("welk deel van de PV is direct verbruikt").
// Last = household + backup, gelijk aan de server (savings-venster) en aan
// teller 33179 (wachtrij #201); `grid_load_w` alleen mist VJ's achterhuis.

import type { HistorySample } from '../api/types';

const STEP_HOURS = 5 / 60;
const NL_TZ = 'Europe/Amsterdam';

/** UTC-offset (ms) van Europe/Amsterdam op tijdstip `t`. */
function nlOffsetMs(t: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: NL_TZ,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(t));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - Math.floor(t / 1000) * 1000;
}

/** Epoch-ms van middernacht (Europe/Amsterdam) van de NL-dag waarin `now` valt. */
export function nlMidnightMs(now: Date): number {
  const t = now.getTime();
  const local = new Date(t + nlOffsetMs(t));
  const utcMidnight = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate());
  // De zomertijdwissel valt om 01:00 UTC, na NL-middernacht: de offset een uur
  // vóór UTC-middernacht is dus de offset die op NL-middernacht gold.
  return utcMidnight - nlOffsetMs(utcMidnight - 3_600_000);
}

/**
 * Directe PV→verbruik (kWh) sinds NL-middernacht, of null als er geen
 * bucket van vandaag in de historie zit (nog aan het laden, of fout).
 */
export function directPvToLoadTodayKwh(
  samples: HistorySample[] | null | undefined,
  now: Date,
): number | null {
  if (!samples || samples.length === 0) return null;
  const start = nlMidnightMs(now);
  let kwh = 0;
  let n = 0;
  for (const s of samples) {
    const t = Date.parse(s.ts);
    if (!Number.isFinite(t) || t < start) continue;
    const pvW = Math.max(0, s.pv_w ?? 0);
    const loadW = Math.max(0, (s.grid_load_w ?? 0) + (s.backup_w ?? 0));
    kwh += (Math.min(pvW, loadW) / 1000) * STEP_HOURS;
    n += 1;
  }
  return n > 0 ? kwh : null;
}
